import React, { useEffect, useState } from "react";
import { DropdownV2, CounterInputV2 } from "../../FormControlsV2";
import "./style.css";
import { ButtonOnlyTitle } from "../../Buttons/ButtonOnlyTitle/Index";
import { MessageBalloon } from "../../MessageBalloon/Index";
import axios from "../../../services/axios";
import { IOptionProps, IOptionsProps } from "../../../@types/reactSelect/IOption";
import { useTaskCreation } from "../../../hooks/useTaskCreation";
import { useTranslation } from "react-i18next";

interface WaterTroughProps { }

type TSpecificTask = "INSTALL" | "REPAIR" | "REMOVE" | undefined;

type TResponse = { id: string; name: string };

export const WaterTroughs: React.FC<WaterTroughProps> = () => {
  const { t } = useTranslation();
  const [specificTask, setSpecificTask] = useState<TSpecificTask>();
  const [createTask, setCreateTask] = useState<boolean>(false);

  const [showMaterials, setShowMaterials] = useState<boolean>(false);
  const [showSizeChooser, setShowSizeChooser] = useState<boolean>(false);

  const [changes, setChanges] = useState<any>();

  const [waterTroughs, setWaterTroughs] = useState<{
    micro_area_resource_id: number;
    resource_name: string;
  }>();
  const [shapeOptions, setShapeOptions] = useState<IOptionsProps>([]);
  const [materialOptions, setMaterialOptions] = useState<IOptionsProps>([]);
  const [selectedShape, setSelectedShape] = useState<IOptionProps | null>(null);
  const [selectedMaterial, setSelectedMaterial] = useState<IOptionProps | null>(null);
  const [accessMeters, setAccessMeters] = useState(0);

  const {
    payload,
    setPayload,
    setAllowTaskCreation,
    responsibleId,
    microAreaId,
  } = useTaskCreation();

  function getButtonThemes(task: TSpecificTask) {
    if (!specificTask) return "dark";

    if (specificTask === task) return "success";
    else return "light";
  }

  const getOptions = (data: Array<TResponse>): IOptionsProps => {
    if (!data) return [];

    return data.map((value) => ({ label: value.name, value: value.id }));
  };

  useEffect(() => {
    getWaterTroughs();
  }, []);

  useEffect(() => {
    if (!specificTask || specificTask !== "INSTALL") return;

    getShapes();
    getMaterials();
  }, [specificTask]);

  const getShapes = () => {
    axios
      .get<Array<TResponse>>("/tasks/water-trough/shapes")
      .then((response) => {
        setShapeOptions(getOptions(response.data));
      });
  };

  const getMaterials = () => {
    axios
      .get<Array<TResponse>>("/tasks/water-trough/materials")
      .then((response) => {
        setMaterialOptions(getOptions(response.data));
      });
  };

  const getWaterTroughs = () => {
    axios
      .get("/micro-areas/resources", {
        params: {
          micro_area_id: microAreaId,
        },
      })
      .then(
        (response: {
          data: [{ micro_area_resource_id: number; resource_name: string }];
        }) => {
          if (!response.data) return;
          setWaterTroughs(
            response.data.find(
              (resource) => resource.resource_name === "Bebedouro"
            )
          );
        }
      );
  };

  function translateActivity(activity: TSpecificTask): string {
    switch (activity) {
      case "INSTALL":
        return "INSTALAR";

      case "REMOVE":
        return "REMOVER";

      case "REPAIR":
        return "REPARAR";

      default:
        return "";
    }
  }

  const handleActivityChoice = (activity: TSpecificTask) => {
    setSpecificTask(activity);
    if (activity !== "INSTALL") setCreateTask(true);
    else setCreateTask(false);

    let nextChanges: any = {
      water_trough_activity: translateActivity(activity),
      micro_area_id: microAreaId,
      assigned_user_id: responsibleId,
    };

    if (waterTroughs && (activity === "REMOVE" || activity === "REPAIR"))
      nextChanges = {
        ...nextChanges,
        micro_area_aditional_resource_id: waterTroughs.micro_area_resource_id,
      };

    setChanges(nextChanges);
    setSelectedShape(null);
    setSelectedMaterial(null);
    setAccessMeters(0);
    setShowMaterials(false);
    setShowSizeChooser(false);
  };

  const handleShapeChoice = (opt: IOptionProps | null) => {
    setSelectedShape(opt);
    const shapeId = opt?.value;
    setShowMaterials(!!shapeId);
    setChanges((prev: any) => ({
      ...prev,
      water_trough_shape_id: shapeId,
    }));
  };

  const handleMaterialChoice = (opt: IOptionProps | null) => {
    setSelectedMaterial(opt);
    const materialId = opt?.value;
    setShowSizeChooser(!!materialId);
    setChanges((prev: any) => ({
      ...prev,
      water_trough_material_id: materialId,
    }));
  };

  const handleSizeInput = (size: number) => {
    setAccessMeters(size);
    setChanges((prev: any) => ({
      ...prev,
      access_area_meters: +size,
    }));

    if (size === 0) setCreateTask(false);
    else setCreateTask(true);
  };

  useEffect(() => {
    if (!createTask) return setAllowTaskCreation(false);

    setPayload({ ...payload, changes: [changes] });
    setAllowTaskCreation(true);
  }, [changes]);

  return (
    <>
      <>
        <div className="balloon-container">
          <MessageBalloon text={t("taskCreation.help.activity")} pointed />
        </div>

        <div
          className="task-button-container"
          title={
            !waterTroughs
              ? t("taskCreation.help.noTroughs")
              : ""
          }
        >
          <ButtonOnlyTitle
            title={t("taskCreation.fields.removeTrough")}
            theme={getButtonThemes("REMOVE")}
            onClick={() => handleActivityChoice("REMOVE")}
          />
        </div>
        <div
          className="task-button-container"
          title={
            !waterTroughs
              ? t("taskCreation.help.noTroughs")
              : ""
          }
        >
          <ButtonOnlyTitle
            title={t("taskCreation.fields.repairTrough")}
            theme={getButtonThemes("REPAIR")}
            onClick={() => handleActivityChoice("REPAIR")}
          />
        </div>
        <div
          className="task-button-container"
          title={
            waterTroughs ? t("taskCreation.help.hasTroughs") : ""
          }
        >
          <div className="task-button-container">
            <ButtonOnlyTitle
              title={t("taskCreation.fields.installTrough")}
              theme={getButtonThemes("INSTALL")}
              onClick={() => handleActivityChoice("INSTALL")}
            />
          </div>
        </div>
      </>
      {specificTask && specificTask === "INSTALL" && (
        <>
          <DropdownV2
            name="water-trough-shape"
            placeholder={t("taskCreation.fields.selectFormat")}
            options={shapeOptions}
            value={selectedShape}
            menuMount="overlay"
            onChange={(opt) => handleShapeChoice(opt)}
            label={t("taskCreation.fields.format")}
          />
          {showMaterials && (
            <>
              <div className="task-question-container">
                <div className="balloon-container">
                  <MessageBalloon
                    text={t("taskCreation.help.moreMaterials")}
                    pointed
                  />
                </div>
                <DropdownV2
                  name="water-trough-material"
                  placeholder={t("taskCreation.fields.selectMaterial")}
                  options={materialOptions}
                  value={selectedMaterial}
                  menuMount="overlay"
                  onChange={(opt) => handleMaterialChoice(opt)}
                />
              </div>
              {showSizeChooser && (
                <div className="task-question-container">
                  <div className="balloon-container">
                    <MessageBalloon
                      text={t("taskCreation.help.accessSize")}
                      pointed
                    />
                  </div>
                  <CounterInputV2
                    name="water-trough-access-m"
                    suffix="m"
                    value={accessMeters}
                    min={0}
                    max={9999}
                    onChange={(v) => handleSizeInput(v)}
                  />
                </div>
              )}
            </>
          )}
        </>
      )}
    </>
  );
};
